import BaseComponent from "@js/components/base_component.jsx";
import EChart from "./echart.jsx";
import HomePending from "./pending.jsx";
import { INK } from "./theme";

// A full-width panel on the home screen: a heading, the window's headline
// figure, and a chart of it — with a toggle to reveal the same numbers as a table, so
// no value on the page is reachable only by hovering a colored mark.
//
// The heading and the window are known before the figures are, so a pending
// panel still states both and holds the height the chart will fill.
//
// Props:
//   title         — the heading, styled to match the configuration cards above
//   subtitle      — the window the figures cover ("Last 7 days")
//   pending       — true while the figures have yet to arrive
//   headline      — the summary block rendered under the heading
//   legend        — [{ label, color, line }], the key for a multi-series chart
//   option        — the ECharts option
//   height        — chart height in pixels
//   tableColumns  — [string]
//   tableTotalRow — [string], the window's totals, pinned above the series
//   tableRows     — [[string]], already formatted
class HomeChartPanel extends BaseComponent {

  constructor(props) {
    super(props);

    this.state = { showTable: false };
  }

  render() {
    const { title, subtitle, headline, pending } = this.props;

    return (
      <div
        className="ibox"
        style={{
          position: "relative",
          margin: 0,
          background: INK.surface,
          border: `1px solid ${INK.border}`,
          borderRadius: 6,
          overflow: "hidden",
        }}
      >
        {/* Out of the flow, so the button's height cannot push the headline
            figure away from its heading — the two have to sit exactly as
            closely as they do on the configuration cards. */}
        {!pending && (
          <div style={{ position: "absolute", top: 12, right: 16 }}>
            {this._renderToggle()}
          </div>
        )}

        {/* Same structure and padding as HomeStatCard's heading block. */}
        <div style={{ padding: "14px 16px 4px" }}>
          <span style={{ color: INK.title, fontSize: 13 }}>{title}</span>
          <small style={{ color: INK.muted, marginLeft: 8 }}>{subtitle}</small>

          {headline}
        </div>

        <div style={{ padding: "0 16px 16px" }}>
          {pending ? <HomePending height={this.props.height} /> : this._renderFigures()}
        </div>
      </div>
    );
  }

  _renderFigures() {
    const legend       = this.props.legend;
    const showingChart = !this.state.showTable;

    return (
      <React.Fragment>
        {showingChart ? this._renderChart() : this._renderTable()}

        {showingChart && legend && legend.length > 0 && this._renderLegend(legend)}
      </React.Fragment>
    );
  }

  _renderToggle() {
    return (
      <button
        type="button"
        className="btn btn-xs btn-white"
        onClick={() => this.setState({ showTable: !this.state.showTable })}
      >
        {this.state.showTable ? I18n.t("home_dashboard.show_chart") : I18n.t("home_dashboard.show_table")}
      </button>
    );
  }

  // The chart's key, under the plot and centered on it. Each entry wears the
  // mark its series is drawn with, so a line series reads as a line rather than
  // another block of color, and carries that series' figure for the window
  // (`detail`) in a lighter ink than its name.
  _renderLegend(legend) {
    return (
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 20,
          marginTop: 10,
        }}
      >
        {legend.map(item => (
          <span key={item.label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: INK.body }}>
            <span
              style={{
                width: item.line ? 14 : 11,
                height: item.line ? 3 : 11,
                borderRadius: 2,
                background: item.color,
                flexShrink: 0,
              }}
            />
            {item.label}

            {item.detail && (
              <span style={{ color: INK.muted, fontVariantNumeric: "tabular-nums" }}>{item.detail}</span>
            )}
          </span>
        ))}
      </div>
    );
  }

  // Unmounted rather than hidden while the table shows: ECharts measures its
  // container when it initializes, and a display:none container measures zero.
  _renderChart() {
    return <EChart option={this.props.option} height={this.props.height} />;
  }

  // The table is the readable-values view of the chart, and it holds a row per
  // bucket — a week of hourly buckets is well past what belongs inline, so it
  // scrolls. The head sticks, and it carries the window's totals as well as the
  // column names, so the figures the panel is really about stay on screen
  // however far down the series you are.
  _renderTable() {
    const { tableColumns, tableTotalRow, tableRows } = this.props;

    return (
      <div style={{ maxHeight: 400, overflow: "auto" }}>
        <table className="table table-condensed" style={{ marginBottom: 0, background: INK.white }}>
          <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
            <tr>
              {tableColumns.map((column, index) => (
                <th key={column} style={{ ...this._cellStyle(index), background: INK.white }}>{column}</th>
              ))}
            </tr>

            {tableTotalRow && (
              <tr>
                {tableTotalRow.map((cell, index) => (
                  <th
                    key={index}
                    style={{
                      ...this._cellStyle(index),
                      background: INK.surface,
                      borderTop: `1px solid ${INK.border}`,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {cell}
                  </th>
                ))}
              </tr>
            )}
          </thead>

          <tbody>
            {/* Keyed by position: two rows can share a label — an hourly chart
                spanning a daylight-saving fall-back has two 1:00 AM hours. */}
            {tableRows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, index) => (
                  <td key={index} style={{ ...this._cellStyle(index), fontVariantNumeric: "tabular-nums" }}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // The first column names the bucket; every other column is a figure, and
  // figures line up on the right.
  _cellStyle(index) {
    return { textAlign: index === 0 ? "left" : "right", whiteSpace: "nowrap" };
  }

}

export default HomeChartPanel;
